/**
 * The cluster → database graph's vocabulary, shared by the read core, the GraphQL
 * resolver, the MCP tool and the web.
 *
 * An EDGE means one thing and it is worth saying before the types: this cluster is
 * ALLOWED to reach this database — a DigitalOcean trusted-source firewall rule or an
 * access-list entry says so. It does NOT mean anything actually connects; no workload
 * environment is read. The colour on the edge is therefore the health of a PERMISSION,
 * derived from the agent fleet, not the health of a connection.
 *
 * These four names live here rather than in the resolver because three surfaces render
 * them (GraphQL, MCP, the tab) and a fifth (`check-cluster-graph.mjs`) pins them. A
 * second copy would eventually disagree about which colour means "nobody knows", and
 * that is precisely the state an operator must not read as "fine".
 */

export const EDGE_HEALTHS = ['green', 'red', 'amber', 'neutral'] as const;
export type EdgeHealth = (typeof EDGE_HEALTHS)[number];

export const EDGE_EVIDENCE = ['k8s_rule', 'ip_rule', 'atlas_entry', 'mapping'] as const;
export type EdgeEvidence = (typeof EDGE_EVIDENCE)[number];

/**
 * The WORD a health badge shows. `neutral` reads "unknown", not "neutral": the value is
 * about our knowledge, not about the database, and every row must carry the word as text
 * — colour alone is not a state anyone can read out loud, or see colour-blind.
 */
export const EDGE_HEALTH_LABEL: Record<EdgeHealth, string> = {
  green: 'green',
  red: 'red',
  amber: 'amber',
  neutral: 'unknown',
};

/**
 * Strongest evidence wins when two facts describe the same pair — a `k8s` rule that
 * admits the whole cluster is a stronger statement than a mapping that merely authorizes
 * the pair. `mapping` is the weakest on purpose: it proves authorization and nothing
 * about any address, so it can never colour an edge green.
 */
export const EDGE_EVIDENCE_RANK: Record<EdgeEvidence, number> = {
  k8s_rule: 4,
  ip_rule: 3,
  atlas_entry: 2,
  mapping: 1,
};

export function isEdgeHealth(v: unknown): v is EdgeHealth {
  return typeof v === 'string' && (EDGE_HEALTHS as readonly string[]).includes(v);
}

export function isEdgeEvidence(v: unknown): v is EdgeEvidence {
  return typeof v === 'string' && (EDGE_EVIDENCE as readonly string[]).includes(v);
}

/** A DigitalOcean managed database, or a MongoDB Atlas project. */
export type ClusterDatabaseKind = 'do' | 'atlas';

/**
 * One row of `cluster_databases` (MCP) — the flat form of one graph edge plus its
 * database node. The GraphQL query splits the same facts across a node and an edge; both
 * carry identical values, and AC-9 compares them database for database.
 */
export interface ClusterDatabaseRow {
  kind: ClusterDatabaseKind;
  /** The `clusterGraph` node id: `db:do:<uuid>` or `db:atlas:<projectKey>`. */
  id: string;
  name: string;
  /** DO engine (`pg`, `valkey`, …) or `atlas`. */
  engine: string | null;
  /** `do_accounts.slug` owning the database; null for an Atlas project. */
  team: string | null;
  /** `atlas_projects.project_key` when registered as a cluster-access target. */
  targetKey: string | null;
  health: EdgeHealth;
  reason: string;
  agentsGood: number;
  agentsTotal: number;
  nodesTotal: number | null;
  evidence: EdgeEvidence;
  /** ISO. null = the rule set / access list behind this edge has never been read. */
  listReadAt: string | null;
}
